/** Read-only category presentation shared by HTML/PDF and editable exports.
 * Reuses the established report-rendering provenance reconciliation and the
 * conclusion table's status policy; it never changes the saved category score,
 * verdict, audit history or report data. */
import type { SiteRagReport, RagCategory, RagStatus } from '../types/siteRag';
import {
  evaluateTopographySlopeProvenance,
  reconcileRenderTimeProvenanceAndVerdictPhrasing,
} from './reportRendering';

export interface ReportCategoryDisplayRow {
  id: string;
  label: string;
  status: RagStatus;
  band: string;
  evidence: string;
}

function hasRedMetricOrHighWaterStress(c: RagCategory): boolean {
  return (
    (c.metrics ?? []).some(
      (m) => m.status === 'red' || (m.id === 'water_stress' && /high|extreme/i.test(String(m.value ?? ''))),
    ) || /Water Stress Index:\s*(?:High|Extreme)\s*\(red\)/i.test(c.explanation ?? '')
  );
}

function hasMissingOrPlanningOnly(c: RagCategory): boolean {
  return (
    (c.metrics ?? []).some((m) => m.missing) ||
    (c.metrics ?? []).some((m) => /planning-default\s+elcc/i.test(`${m.label ?? ''} ${m.value ?? ''}`)) ||
    /evidence gap\s*[—–-]\s*excluded from scoring|planning-default\s+elcc/i.test(c.explanation ?? '')
  );
}

function effectiveStatus(c: RagCategory): RagStatus {
  if (c.status === 'green' && (hasRedMetricOrHighWaterStress(c) || hasMissingOrPlanningOnly(c))) {
    return 'amber';
  }
  return c.status;
}

/** Supporting band word for the status dot (matches the radar's ladder). */
function statusBand(c: RagCategory): string {
  switch (c.status) {
    case 'green': {
      if (c.id === 'cooling' && hasRedMetricOrHighWaterStress(c)) {
        return 'Conditional (climate favorable; supply/design unverified)';
      }
      if (c.id === 'regulatory' && hasMissingOrPlanningOnly(c)) {
        return 'Conditional (state proxy; local zoning unverified)';
      }
      return hasMissingOrPlanningOnly(c) || hasRedMetricOrHighWaterStress(c)
        ? 'Conditional (partial basis)'
        : 'Strong';
    }
    case 'amber': return 'Watch';
    case 'red': return 'Risk';
    default: return '—';
  }
}

/** Display-layer guard: round raw floats that leak into evidence strings
 *  (e.g. "1170.1054573547272 acres", "10.245000000000001 ms") to a sane
 *  precision — ≥100 → integer, else 1 dp. Defensive; the upstream evidence
 *  builders should also round, but this catches any that don't. */
export function sanitizeEvidenceNumbers(s: string): string {
  return s.replace(/-?\d+\.\d{3,}/g, (m) => {
    const n = Number(m);
    if (!Number.isFinite(n)) return m;
    const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
    return String(rounded);
  });
}

function hasSinglePointSlope(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).singlePointZeroSlope;
}

function hasUnverifiedZeroSlopeProxy(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).unverifiedZeroSlopeNoProvenance;
}

function hasUnverified3DepSlopeProxy(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).unverified3DepSlopeProxy;
}

/** First non-empty sentence/line of the category explanation (kept concise for
 *  the evidence column); falls back to a status-derived line when absent. */
function justifyingEvidence(c: RagCategory, report: SiteRagReport): string {
  let raw = c.explanation?.trim();
  if (raw) {
    if (c.id === 'site' && hasSinglePointSlope(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: slope unverified (single-point elevation sample)',
      );
    } else if (c.id === 'site' && hasUnverifiedZeroSlopeProxy(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: slope unverified (0% elevation-relief proxy without bound survey provenance – excluded from scoring)',
      );
    } else if (c.id === 'site' && hasUnverified3DepSlopeProxy(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*(\d+(?:\.\d+)?)\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: $1% regional relief proxy (amber — local slope derivation unverified; site topographic survey required)',
      );
    }
    return sanitizeEvidenceNumbers(raw);
  }
  switch (c.status) {
    case 'green': return 'Supported by the provider/SED evidence for this category.';
    case 'amber': return 'Mixed or incomplete evidence — verify before final approval.';
    case 'red': return 'Material gap or risk flagged in this category.';
    default: return '—';
  }
}

/** Return the same evidence qualifications for every report surface. */
export function buildReportCategoryDisplayRows(report: SiteRagReport): ReportCategoryDisplayRow[] {
  const displayReport = reconcileRenderTimeProvenanceAndVerdictPhrasing(report);
  return (displayReport.categories ?? []).map((category) => ({
    id: category.id,
    label: category.label,
    status: effectiveStatus(category),
    band: statusBand(category),
    evidence: justifyingEvidence(category, displayReport),
  }));
}
